/**
 * Placement for the floating slot action pill.
 *
 * Mirrors `computePillPlacement` in react-native/src/components/SlotActionPill.tsx —
 * keep the two in sync.
 */

/** The active slot's frame, in the pill host's coordinates. */
export interface SlotAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
  hostWidth: number;
  hostHeight: number;
}

export interface PillPlacement {
  left: number;
  top: number;
  /** Caret centre, relative to the pill's left edge. */
  caretX: number;
  /** Which side of the slot the pill sits on; 'overlap' when neither fits (no caret). */
  side: 'above' | 'below' | 'overlap';
}

interface PlacementMetrics {
  /** Minimum distance from the host's edges. */
  edge: number;
  /** Space between the slot and the pill body (the caret sits in it). */
  gap: number;
  /** Closest the caret may get to the pill's ends (clears the rounded corners). */
  caretInset: number;
}

/** Less than this much of the slot on screen and the pill has nothing to point at. */
const MIN_VISIBLE_SLOT = 12;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Place the pill centred over the visible part of the slot, kept inside the host
 * horizontally (the caret slides to keep pointing at the slot), above the slot
 * when it fits, else below, else overlapping it. Returns null when the slot is
 * scrolled out of view.
 */
export const computePillPlacement = (
  anchor: SlotAnchor,
  pill: { width: number; height: number },
  { edge, gap, caretInset }: PlacementMetrics,
): PillPlacement | null => {
  const visibleLeft = Math.max(anchor.x, 0);
  const visibleRight = Math.min(anchor.x + anchor.width, anchor.hostWidth);
  const visibleTop = Math.max(anchor.y, 0);
  const visibleBottom = Math.min(anchor.y + anchor.height, anchor.hostHeight);
  if (visibleRight - visibleLeft < MIN_VISIBLE_SLOT || visibleBottom - visibleTop < MIN_VISIBLE_SLOT) {
    return null;
  }

  const center = (visibleLeft + visibleRight) / 2;
  const left = clamp(center - pill.width / 2, edge, Math.max(edge, anchor.hostWidth - edge - pill.width));
  const caretX = clamp(center - left, caretInset, pill.width - caretInset);

  const aboveTop = anchor.y - gap - pill.height;
  if (aboveTop >= edge) {
    return { left, top: aboveTop, caretX, side: 'above' };
  }
  const belowTop = anchor.y + anchor.height + gap;
  if (belowTop + pill.height <= anchor.hostHeight - edge) {
    return { left, top: belowTop, caretX, side: 'below' };
  }
  const top = clamp(visibleTop, edge, Math.max(edge, anchor.hostHeight - edge - pill.height));
  return { left, top, caretX, side: 'overlap' };
};
